import React from "react";
import {
  Animated,
  Dimensions,
  Easing,
  ScrollView,
  Platform
} from "react-native";
import styled, { css, withTheme } from "styled-components/native";

const DEVICE_WIDTH = parseFloat(Dimensions.get("window").width);

/* General layout for the overlay and the drawer */
const wrapper = css`
  position: absolute;
  top: 0;
  bottom: 0;
`;

/* Layout for the overlay which dims the background */
const Overlay = styled(Animated.View)`
  ${wrapper}
  background-color: ${props => props.theme.overlayColor};
  left: 0;
  right: 0;
  z-index: 25;
`;

/* Layout for the drawer which displays the menu */
const Drawer = styled(Animated.View)`
  ${wrapper}
  flex-direction: row;
  z-index: 50;
`;

/* Web does not support native animations, so we only enable for mobile devices */
const useNativeDriver = Platform.OS !== "web";

/**
 * Animates the display of a drawer from the left hand side, it can be used to display a menu or some other information originating from the left.
 */
class StickyDrawer extends React.PureComponent {
  constructor(props) {
    super(props);

    /**
     * Duration of the animation to show the drawer and to dim the background.
     */
    this.duration = 500;

    /**
     * Total number of columns that is currently shown in the drawer, which by default is 1.
     */
    this.totalColumns = 1;

    this.isNarrow = false;

    this.stateChanged = this.stateChanged.bind(this);
    this.show = this.show.bind(this);
    this.onShown = this.onShown.bind(this);
    this.hide = this.hide.bind(this);
    this.onHidden = this.onHidden.bind(this);
    this.push = this.push.bind(this);
    this.reset = this.reset.bind(this);
    this.onLayout = this.onLayout.bind(this);
    this.onItemAdded = this.onItemAdded.bind(this);
    this.state = {
      showOverlay: props.isOpen,

      //Children that have been added dynamically to the component via the push method
      children: [],

      //Width of each column in the drawer
      columnWidth: props.initialWidth
    };

    //Configure the values that will animate our components
    this.position = new Animated.Value(props.isOpen ? 0 : -(DEVICE_WIDTH));
    this.overlayOpacity = new Animated.Value(props.isOpen ? 0.7 : 0);
    this.drawerOpacity = new Animated.Value(props.isOpen ? 1 : 0);
    this.width = new Animated.Value(this.columnWidth || 0);
  }

  // TODO This event is going to be deprecated, we need a better solution to this.
  componentWillReceiveProps(props) {
    if (props.isOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Fires the onStateChanged prop to indicate if the drawer is opened or closed (shown or hidden).
   */
  stateChanged(isOpen) {
    if (this.props.onStateChanged) {
      this.props.onStateChanged(isOpen);
    }
  }

  /**
   * Expands the width of the drawer to fit the specified columns.
   *
   * Internally what we do is multiply the number of columns times the prop columnWidth to determine the width of the drawer.
   */
  expand(columns) {
    this.totalColumns = columns;

    Animated.timing(this.width, {
      // If the totalColumns * initialWidth exceeds the width of the screen then we use the screen width and simply scroll to the end
      toValue:
        this.props.initialWidth * this.totalColumns > DEVICE_WIDTH
          ? DEVICE_WIDTH
          : this.props.initialWidth * this.totalColumns,
      duration: this.duration,
      easing: Easing.out(Easing.quad)
    }).start();
  }

  /**
   * Push an additional column to the drawer.
   *
   * originColumn is the column index that is requesting that the new column be added.
   * item is the React component that must be added to the drawer.
   */
  push(originColumn, item) {
    let children = [...this.state.children, item];

    //Remove the items beyond the originating column (which may be out of bounds)
    children.splice(originColumn + 1);

    //Make the drawer grow in width based on the total number of columns
    this.expand(children.length + 1);

    this.setState(
      {
        children
      },
      () => {
        // For mobile this event is being handled by the the ScrollView onContentSizeChange prop
        if (Platform.OS === "web") {
          this.onItemAdded();
        }
      }
    );
  }

  /**
   * Reset the drawer to the original state before new columns were pushed in.
   */
  reset() {
    this.expand(1);
    this.setState({
      children: []
    });
  }

  /**
   * Reveals the drawer with an animation.
   */
  show() {
    this.setState({ showOverlay: true });
    this.animating = true;
    this.stateChanged(true);

    //Make sure the menu has been reset when a new column was added in previous interaction with the drawer
    this.reset();

    Animated.parallel([
      Animated.timing(this.drawerOpacity, {
        toValue: 1,
        duration: this.duration,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      }),

      Animated.timing(this.position, {
        toValue: 0,
        duration: this.duration,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      }),

      Animated.timing(this.overlayOpacity, {
        toValue: 0.7,
        duration: this.duration,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      })
    ]).start(this.onShown);
  }

  /**
   * Fired when the show animation is finished.
   */
  onShown() {
    this.animating = false;
  }

  /**
   * Hides the drawer from the user's view with animation.
   */
  hide() {
    this.stateChanged(false);

    Animated.parallel([
      Animated.timing(this.width, {
        toValue: this.props.initialWidth,
        duration: this.duration,
        easing: Easing.out(Easing.quad)
      }),

      Animated.timing(this.drawerOpacity, {
        toValue: 0,
        duration: this.duration,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      }),

      Animated.timing(this.overlayOpacity, {
        toValue: 0,
        duration: this.duration,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      }),

      Animated.timing(this.position, {
        toValue: -(DEVICE_WIDTH),
        duration: this.duration * 4,
        easing: Easing.out(Easing.quad),
        useNativeDriver
      })
    ]).start(this.onHidden);
  }

  /**
   * Fired when the hide animation is finished.
   */
  onHidden() {
    if (!this.animating) {
      // This is done so that the overlay is not out of sync when the menu button is pressed too quickly
      this.setState({ showOverlay: false });

      this.animating = false;
    }
  }

  /**
   * Manages the responsiveness of our component.
   *
   * If the drawer is opened on a narrow device (like a phone) then the full width of the device is used.
   */
  onLayout(e) {
    const { props } = this,
      width = e.nativeEvent.layout.width;

    // This 768 is the lg breakpoint in react-native-responsive-layout
    if (width > 0 && width < 768) {
      //Device is narrow so we use the full width
      this.setState({ columnWidth: width });
      this.width.setValue(width);
      this.isNarrow = true;
    } else {
      //Device is wide (like a desktop or tablet) so we set the drawer width based on the number of columns that the drawer has
      this.setState({ columnWidth: props.initialWidth });
      this.width.setValue(
        // If the totalColumns * initialWidth exceeds the width of the screen then we use the screen width and simply scroll to the end
        props.initialWidth * this.totalColumns > width
          ? width
          : props.initialWidth * this.totalColumns
      );
      this.isNarrow = false;
    }
  }

  /**
   * Fired when a new column is added to the drawer.
   */
  onItemAdded() {
    if (
      this.isNarrow === true ||
      (this.isNarrow === false &&
        this.props.initialWidth * this.totalColumns > DEVICE_WIDTH)
    ) {
      //This makes our ScrollView move to the right (when the screen is narrow) with animation
      this.refs.columnsView.scrollToEnd();
    }
  }

  render() {
    const { props, state } = this;

    return (
      <React.Fragment>
        {/* Overlay */}
        {/* When web support is added to react-native-blur, we replace it instead of the opacity: https://github.com/react-native-community/react-native-blur/pull/190 */}
        {/* This is needed so that the items below the overlay can be interacted (tapped) when the drawer isn't shown */}
        {state.showOverlay &&
        <Overlay
            onLayout={this.onLayout}
            style={{
              opacity: this.overlayOpacity
            }}
        />
        }

        {/* Drawer container which has the position and animation */}
        <Drawer
          style={[
            props.style,
            {
              // This is done because react-native does NOT suppport animating the width of a View
              width: !this.isNarrow && Platform.OS === "web" ? this.width : this.state.columnWidth,
              transform: [{ translateX: this.position }],
              opacity: this.drawerOpacity
            }
          ]}
        >
          {/* Drawer which contains the columns and is in charge of the horizontal scrolling on narrow screens */}
          <ScrollView
            ref="columnsView"
            pagingEnabled={true}
            horizontal={true}
            bounces={false}
            style={{ flex: 1 }}
            onContentSizeChange={this.onItemAdded}
            contentContainerStyle={{
              width:
                this.isNarrow ? this.state.columnWidth : this.props.initialWidth * this.totalColumns
            }}
          >
            {/* These are the children passed in initially */}
            {props.children({ columnWidth: state.columnWidth })}

            {/* These are the columns dynamically added to the drawer */}
            {state.children}
          </ScrollView>
        </Drawer>
      </React.Fragment>
    );
  }
}

export default withTheme(StickyDrawer);
